import { ComponentType, createContext, useContext } from 'react';
import {
  SidePanelPropertyDescriptor,
  SidePanelPropertyKey,
  SidePanelRowProps,
} from 'src/graphql/entities/side-panel-personalization';
import { CUSTOM_ATTRIBUTE_TYPE_ICONS } from './custom-attributes.mappings';
import {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from './custom-attributes.types';
import { CustomAttributeRowData } from './field-view-registry/types';

export interface CustomAttributesSidePanelData {
  rowsByPropertyKey: Map<SidePanelPropertyKey, CustomAttributeRowData>;
  loading: boolean;
  payloadError: string | null;
  disabled: boolean;
  onSave: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => void;
}

export const CustomAttributesSidePanelContext =
  createContext<CustomAttributesSidePanelData | null>(null);

export function useCustomAttributesSidePanel(): CustomAttributesSidePanelData {
  const value = useContext(CustomAttributesSidePanelContext);

  if (!value) {
    throw new Error(
      'useCustomAttributesSidePanel must be used within CustomAttributesSidePanelProvider',
    );
  }

  return value;
}

export function buildCustomAttributeSidePanelDescriptors(
  rowsByPropertyKey: Map<SidePanelPropertyKey, CustomAttributeRowData>,
  Row: ComponentType<SidePanelRowProps>,
): SidePanelPropertyDescriptor[] {
  return [...rowsByPropertyKey.entries()].map(([key, row]) => ({
    key,
    label: row.definition.label,
    group: row.definition.groupKey
      ? {
          key: row.definition.groupKey,
          label: row.definition.groupLabel ?? row.definition.groupKey,
        }
      : undefined,
    defaultVisible: false,
    Row,
    isEmpty: row.isEmpty,
    icon: CUSTOM_ATTRIBUTE_TYPE_ICONS[row.definition.type],
  }));
}
